'use client';

import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';

import { cn } from '@/lib/cn';
import type { AuthSession } from '../../../lib/authSession';
import type { BillingExposure } from '../../../hooks/useBillingExposure';
import type { ThemeMode } from '../../ThemeToggle';
import { UserMenuPopover } from '../Sidebar/UserMenuPopover';

type Translate = (key: string) => string;

type TopBarUserMenuProps = {
  session: AuthSession | null;
  billingExposure: BillingExposure;
  tNav: Translate;
  tCommon: Translate;
  tBilling: Translate;
  themeMode: ThemeMode;
  onSelectThemeMode: (mode: ThemeMode) => void;
  onOpenProfile: () => void;
  onOpenSubscribeModal: () => void;
  onOpenSubscriptionModal: () => void;
  onOpenExecutionEnvironment: () => void;
  onOpenOnboardingGuide: () => void;
  onOpenFeedback: () => void;
  onOpenAdminMenu: () => void;
  onLogout: () => void;
};

export const TopBarUserMenu = ({
  session,
  billingExposure,
  tNav,
  tCommon,
  tBilling,
  themeMode,
  onSelectThemeMode,
  onOpenProfile,
  onOpenSubscribeModal,
  onOpenSubscriptionModal,
  onOpenExecutionEnvironment,
  onOpenOnboardingGuide,
  onOpenFeedback,
  onOpenAdminMenu,
  onLogout,
}: TopBarUserMenuProps) => {
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    strategy: 'fixed',
    middleware: [offset(8), flip({ padding: 12 }), shift({ padding: 12 })],
    whileElementsMounted: autoUpdate,
  });

  const initial = (session?.member.nickname?.charAt(0) ?? 'G').toUpperCase();
  const isTrial = billingExposure.subscriptionStatus === 'TRIALING';
  // Hold the Pro marker until the subscription status resolves, otherwise a
  // trialing Pro member briefly flashes "P" before settling on "T".
  const isPro = session?.member.tier === 'PRO' && !billingExposure.subscriptionLoading;
  const nickname = session?.member.nickname ?? tCommon('guest');

  return (
    <Popover className="relative">
      <PopoverButton
        ref={refs.setReference}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full outline-none transition-colors duration-150',
          'hover:bg-[var(--color-bg-hover)]',
          'focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]',
        )}
        aria-label={tNav('signedInUser')}
        title={nickname}
      >
        <span
          aria-hidden="true"
          className={cn(
            'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold',
            'bg-[var(--color-primary-soft-bg)] text-[var(--color-primary)]',
          )}
        >
          {initial}
          {isTrial ? (
            <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-warning/20 text-[8px] font-bold leading-none text-warning ring-1 ring-base-100">
              T
            </span>
          ) : isPro ? (
            <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-success/20 text-[8px] font-bold leading-none text-emerald-800 dark:bg-success/25 dark:text-success ring-1 ring-base-100">
              P
            </span>
          ) : null}
        </span>
      </PopoverButton>

      <FloatingPortal>
        <PopoverPanel ref={refs.setFloating} style={floatingStyles} className="z-[80]">
          {({ close }) => (
            <UserMenuPopover
              session={session}
              billingExposure={billingExposure}
              tNav={tNav}
              tCommon={tCommon}
              tBilling={tBilling}
              themeMode={themeMode}
              onClose={close}
              onSelectThemeMode={onSelectThemeMode}
              onOpenProfile={onOpenProfile}
              onOpenSubscribeModal={onOpenSubscribeModal}
              onOpenSubscriptionModal={onOpenSubscriptionModal}
              onOpenExecutionEnvironment={onOpenExecutionEnvironment}
              onOpenOnboardingGuide={onOpenOnboardingGuide}
              onOpenFeedback={onOpenFeedback}
              onOpenAdminMenu={onOpenAdminMenu}
              onLogout={onLogout}
            />
          )}
        </PopoverPanel>
      </FloatingPortal>
    </Popover>
  );
};
